// Single source of truth for every tunable game number. Nothing in the engine,
// the Cloud Functions, or the web app may hardcode these — always read CONFIG.
//
// See the game spec §12. Changing the game's feel happens here and nowhere else.
export const CONFIG = {
    // ── Build-Your-Card (football) ──────────────────────────────────────────────
    /** Football is a bet-builder. For each fixture you enter you build a CARD: a 1X2
     *  `outcome` ANCHOR (the qualifier) plus 0..MAX_LEGS multiplier LEGS you pick
     *  yourself from a coverage-gated catalog. There is no confidence lever — the only
     *  risk dial is which legs you dare to stack. Scoring (see scoring.ts `cardScore`):
     *
     *    anchor WRONG → −BASE_POINTS                       (a real loss; going wide is a gamble)
     *    anchor RIGHT → round( BASE_POINTS × Π legFactor )
     *                     leg correct     → factor = multiplier
     *                     leg wrong       → factor = multiplier^(−WRONG_LEG_EXPONENT)
     *                     leg ungradeable → factor = 1  (excluded, like a void market)
     *    void fixture → card excluded (contributes 0, never −BASE)
     *
     *  Round score = the sum of every card. Multipliers are FIXED and shown up front
     *  (never live odds): a correct call scores the same whoever was favourite — your
     *  edge is reading that a generically-hard leg is actually likely for THIS match. */
    COMBO: {
        /** The flat points a correct 1X2 anchor banks before any leg multiplies it. */
        BASE_POINTS: 50,
        /** Max self-chosen multiplier legs per card. */
        MAX_LEGS: 5,
        /** How hard a WRONG leg bites, as the exponent on `1/multiplier`. 1 = harsh
         *  (full `1/m`), 0.5 = gentle, 0 = no penalty (a wrong leg is just ×1). The only
         *  knob for the "how much does greed hurt" feel; tune here, never in code. */
        WRONG_LEG_EXPONENT: 1,
        /** Fixed difficulty multiplier per leg market, calibrated from real base rates so a
         *  no-edge pick is ~EV-neutral and your only edge is reading the matchup. The
         *  EV-fair value of a pick that's correct with probability `p` is `(1−p)/p`
         *  (with WRONG_LEG_EXPONENT = 1); easy picks floor near ~1.2 (a hit must never
         *  shrink a card), hard ones pay big. A value is EITHER a flat `number` (the same
         *  for whichever side you back — only sensible when the sides are ~symmetric or
         *  free-form like exact/scorer) OR a per-OUTCOME map keyed by the option value, so
         *  an asymmetric market prices each side by its OWN likelihood (e.g. backing a red
         *  card ~18% pays ×4.5 while "no red card" ~82% is a near-flat ×1.2). The `outcome`
         *  anchor is NOT here — it's the base, not a leg. `assertConfigInvariants` +
         *  the registry invariant enforce every leg/outcome resolves to a multiplier > 1. */
        MULTIPLIERS: {
            // ── Flat (symmetric sides, or free-form exact/player) ──────────────────────
            btts: 1.4, // ~55% BTTS top-5 / ~45% World Cup
            totalGoals: 1.4, // O/U 2.5 ≈ 53/47
            totalCorners: 1.4, // O/U 9.5 ≈ 52/48
            goalBothHalves: 1.5, // ~57%
            scorer: 3.0, // anytime: ~25–50% by player (flat for now; slightly +EV vs the ~29% fair line to reward nailing the scorer)
            exactScore: 7.0, // modal 1-1 ≈ 12%
            // ── Per-outcome (asymmetric: each side priced by its own base rate) ────────
            cleanSheet: { HOME: 2.2, AWAY: 3.6 }, // a side keeps it clean: home ~33%, away ~22% (away CS is the bold, juicy pick)
            firstHalfGoal: { YES: 1.2, NO: 2.3 }, // a 1H goal lands ~70%
            winToNil: { YES: 1.9, NO: 1.3 }, // a side wins to nil ~35%
            redCard: { YES: 4.5, NO: 1.2 }, // ≥1 red ~18%
            firstHalfBtts: { YES: 4.8, NO: 1.2 }, // both score by HT ~17%
            winningMargin: { DRAW: 2.8, ONE: 1.7, TWO_PLUS: 1.6 }, // draw ~25%, by-1 ~37%, by-2+ ~38%
            totalGoalsBand: { B0_1: 2.4, B2_3: 1.3, B4: 3.2 }, // 0–1 ~29%, 2–3 ~47%, 4+ ~24%
            htResult: { HOME: 1.6, DRAW: 1.7, AWAY: 3.0 }, // HT home ~38%, draw ~37%, away ~25%
            highestScoringHalf: { FIRST: 2.0, SECOND: 1.4, EQUAL: 3.0 }, // 2nd ~42%, 1st ~33%, equal ~25%
            mostCards: { HOME: 2.0, DRAW: 4.5, AWAY: 1.5 }, // away see more cards; an equal count is rare (~17%) so it pays bold
            mostCorners: { HOME: 1.3, DRAW: 8.0, AWAY: 1.6 } // home edge in corners; an equal count is very rare (~10%) so it pays big
        }
    },
    /** Over/Under line for the totalGoals market. */
    TOTAL_GOALS_LINE: 2.5,
    /** Over/Under line for the totalCorners market (stats-gated). */
    TOTAL_CORNERS_LINE: 9.5,
    // ── Division / ladder ───────────────────────────────────────────────────---
    /** Ordered tiers, index 0 = lowest. Stored on Membership as the index. */
    TIERS: ['BRONZE', 'SILVER', 'GOLD', 'PLATINUM', 'DIAMOND', 'ELITE'],
    /** Target players per round-group. The last group may be smaller. */
    DIVISION_SIZE: 25,
    PROMOTE_COUNT: 5,
    RELEGATE_COUNT: 5,
    // ── Round timing ─────────────────────────────────────────────────────────--
    /** Promotion/relegation resolves once per round. */
    COMPETITION_PERIOD: 'ROUND',
    /** Open the round this far before its first kickoff (default ~48h). */
    ROUND_OPEN_LEAD_MS: 1000 * 60 * 60 * 48,
    /** Send the one-shot "picks lock soon" reminder this far before first kickoff
     *  (default ~3h) to members who haven't submitted yet. */
    PICK_REMINDER_LEAD_MS: 1000 * 60 * 60 * 3,
    // ── Void handling ──────────────────────────────────────────────────────────
    /** A void fixture's pick contributes 0 and is excluded from the round score. */
    VOID_EXCLUDES_PICK: true,
    // ── Cycling (rider duels) ──────────────────────────────────────────────────
    /** Cycling is the second sport. A "round" is one stage (a one-day race = a single
     *  round); the picked unit is a rider-vs-rider DUEL ("who finishes better?").
     *  Unlike football you don't choose WHICH to enter — the stage presents exactly
     *  DUELS_PER_STAGE balanced, terrain-matched duels and you predict ALL of them,
     *  then spread the confidence ladder across them as a permutation. The division
     *  ladder (TIERS, DIVISION_SIZE, PROMOTE/RELEGATE_COUNT above) is sport-agnostic
     *  and reused as-is; only the scoring shape differs, and it all lives here. */
    CYCLING: {
        /** Balanced duels offered per stage. You predict every one (no "best N of many"). */
        DUELS_PER_STAGE: 6,
        /** = DUELS_PER_STAGE — you pick all duels, so K equals the slate size. */
        PICKS_REQUIRED: 6,
        /** Full descending conviction ladder, assigned as a permutation across the 6
         *  duels: your banker scores ×6, your coin-flip ×1. The only fairness lever —
         *  a correct call scores the same whoever was favourite (never odds-weighted). */
        CONFIDENCE_WEIGHTS: [6, 5, 4, 3, 2, 1],
        CONFIDENCE_MODE: 'MULTIPLY',
        /** Base points for correctly calling a duel, before confidence. */
        POINTS: { duel: 10 },
        /** A void duel (a rider missing/DNF on both sides, or a push) contributes 0 and
         *  is excluded from the stage score — the same rule football uses for voids. */
        VOID_EXCLUDES_PICK: true,
        /** How long AFTER a stage's depart its picks lock (default ~2h). Unlike football,
         *  a cycling duel is decided at the FINISH — hours after the riders roll out — and
         *  the early kilometres usually aren't even televised, so locking right at the
         *  depart shuts players out for no competitive reason. Holding the lock a couple
         *  of hours into the stage lets far more members get their calls in while still
         *  closing comfortably before the result is known. Safe because the conservative
         *  settlement gate never settles until a clean, complete finishing table exists,
         *  so a later lock can never mis-score; it only widens the pick window.
         *
         *  NB: this is a fixed offset from the depart (we don't have per-stage finish
         *  times), tuned to stay well clear of a typical 4–6h WorldTour road stage. Keep
         *  it modest so it can't drift past a short stage's finish. Set to 0 to restore
         *  the old "lock exactly at the depart" behaviour. */
        PICK_LOCK_DELAY_MS: 1000 * 60 * 60 * 2
    },
    // ── Social: head-to-head duels ───────────────────────────────────────────---
    /** A duel reuses the round's own scoring — it adds NO new market or weighting.
     *  It simply compares two members' confidence-weighted round scores. */
    DUEL: {
        /** Length of a friend invite / mini-league join code. */
        INVITE_CODE_LENGTH: 6
    },
    // ── Social: private (friends) mini leagues ─────────────────────────────────-
    /** A mini league is a vanity table over a base league, ranked by each member's
     *  `seasonPoints`. It adds NO new scoring — it never gates tier or matchmaking. */
    MINI_LEAGUE: {
        /** Max characters in a mini-league name. */
        NAME_MAX_LENGTH: 24,
        /** Selectable icon keys; web maps each to a glyph. First entry is the default. */
        ICONS: ['trophy', 'medal', 'flame', 'mountains', 'star'],
        /** Selectable colour keys; web maps each to a gradient. First entry is the default. */
        COLORS: ['green', 'purple', 'blue', 'orange', 'pink', 'graphite']
    },
    // ── Push notifications ─────────────────────────────────────────────────────-
    PUSH: {
        /** Max FCM device tokens kept per user (oldest are dropped beyond this). */
        MAX_TOKENS_PER_USER: 10
    },
    // ── Achievements (vanity badges derived from Membership history) ────────────-
    /** Thresholds are tunable here; the badge set itself is fixed. Earned purely
     *  from vanity history — they never gate tier or matchmaking. */
    ACHIEVEMENTS: {
        /** Exact Master: lifetime exact-score hits at/over this count. */
        EXACT_MASTER_HITS: 10,
        /** Hot Streak: consecutive rounds without relegation at/over this count. */
        HOT_STREAK_ROUNDS: 5,
        /** Climber: lifetime promotions at/over this count. */
        CLIMBER_PROMOTIONS: 3
        /** Reach Elite: highestTierReached === TOP_TIER (no number needed). */
    }
};
/**
 * Startup invariants: turn a misconfiguration into a loud, early crash rather than
 * silent unfair scoring. The football Build-Your-Card numbers must be sane, and the
 * cycling duel ladder must line up with the slate. (The "every leg market has a
 * multiplier" check lives in markets.ts, which can see the registry without a
 * circular import.)
 */
export function assertConfigInvariants() {
    const c = CONFIG.COMBO;
    if (!(c.BASE_POINTS > 0)) {
        throw new Error(`CONFIG invariant violated: COMBO.BASE_POINTS (${c.BASE_POINTS}) must be > 0.`);
    }
    if (!(c.MAX_LEGS >= 1)) {
        throw new Error(`CONFIG invariant violated: COMBO.MAX_LEGS (${c.MAX_LEGS}) must be >= 1.`);
    }
    if (!(c.WRONG_LEG_EXPONENT >= 0)) {
        throw new Error(`CONFIG invariant violated: COMBO.WRONG_LEG_EXPONENT (${c.WRONG_LEG_EXPONENT}) must be >= 0.`);
    }
    for (const [id, v] of Object.entries(c.MULTIPLIERS)) {
        if (typeof v === 'number') {
            if (!(v > 1)) {
                throw new Error(`CONFIG invariant violated: COMBO.MULTIPLIERS.${id} (${v}) must be > 1.`);
            }
        }
        else {
            const entries = Object.entries(v);
            if (entries.length === 0) {
                throw new Error(`CONFIG invariant violated: COMBO.MULTIPLIERS.${id} is an empty per-outcome map.`);
            }
            for (const [outcome, mm] of entries) {
                if (!(mm > 1)) {
                    throw new Error(`CONFIG invariant violated: COMBO.MULTIPLIERS.${id}.${outcome} (${mm}) must be > 1.`);
                }
            }
        }
    }
    // Cycling profile: you predict every duel, so the confidence ladder must carry
    // exactly one weight per duel and the slate size must equal the pick count.
    const cy = CONFIG.CYCLING;
    if (cy.CONFIDENCE_WEIGHTS.length !== cy.PICKS_REQUIRED) {
        throw new Error(`CONFIG invariant violated: CYCLING.CONFIDENCE_WEIGHTS.length (${cy.CONFIDENCE_WEIGHTS.length}) ` +
            `must equal CYCLING.PICKS_REQUIRED (${cy.PICKS_REQUIRED}).`);
    }
    if (cy.PICKS_REQUIRED !== cy.DUELS_PER_STAGE) {
        throw new Error(`CONFIG invariant violated: CYCLING.PICKS_REQUIRED (${cy.PICKS_REQUIRED}) must equal ` +
            `CYCLING.DUELS_PER_STAGE (${cy.DUELS_PER_STAGE}) — every duel is predicted.`);
    }
}
assertConfigInvariants();
/** The display name for a tier index (e.g. 0 → "BRONZE"). Clamps out of range. */
export function tierName(index) {
    const i = Math.max(0, Math.min(CONFIG.TIERS.length - 1, Math.round(index)));
    return CONFIG.TIERS[i];
}
/** Top tier index (e.g. ELITE). */
export const TOP_TIER = CONFIG.TIERS.length - 1;
/** Lowest tier index (BRONZE). */
export const BOTTOM_TIER = 0;
/** The flat multiplier for a market id, or 0 when it's per-outcome / unknown. */
export function flatMultiplier(id) {
    const v = CONFIG.COMBO.MULTIPLIERS[id];
    return typeof v === 'number' ? v : 0;
}
/** The multiplier for a specific outcome of a market: the flat value for symmetric
 *  markets (selection ignored), else the per-outcome map entry. 0 when unresolved. */
export function outcomeMultiplier(id, value) {
    const v = CONFIG.COMBO.MULTIPLIERS[id];
    if (typeof v === 'number')
        return v;
    return v?.[value] ?? 0;
}
//# sourceMappingURL=config.js.map